<template>
  <div class="backend-category-tree">
    <div class="search-box" v-if="showSearch">
      <input
        type="text"
        v-model="searchQuery"
        placeholder="Search categories..."
        :disabled="disabled"
      />
    </div>
    <div class="tree-container">
      <TreeNode
        v-for="category in filteredCategories"
        :key="category.name"
        :node="category"
        :selected-values="selectedValues"
        :search-query="searchQuery"
        :disabled="disabled"
        :counts="counts"
        :parent-path="null"
        @toggle-select="handleToggleSelect"
      />
    </div>
  </div>
</template>

<script>
import { h } from 'vue';

const TreeNode = {
  name: 'TreeNode',
  props: ['node', 'selectedValues', 'searchQuery', 'disabled', 'counts', 'parentPath'],
  data() {
    return {
      isExpanded: false
    };
  },
  computed: {
    hasSubcategories() {
      return this.node.subcategories && this.node.subcategories.length > 0;
    },
    fullPath() {
      // Build the full path by combining parent path with current node name
      return this.parentPath 
        ? `${this.parentPath}|${this.node.name}`
        : this.node.name;
    },
    isSelected() {
      // Check if the full path is in selected values
      return this.selectedValues.includes(this.fullPath);
    },
    count() {
      // Use full path to get the count
      return this.counts[this.fullPath] || 0;
    },
    displayName() {
      const name = this.node.name || '';
      return name.length > 25 ? name.substring(0, 25) + '...' : name;
    }
  },
  watch: {
    searchQuery(newVal) {
      if (newVal && this.hasSubcategories) {
        this.isExpanded = true;
      }
    }
  },
  methods: {
    toggleExpand() {
      if (this.hasSubcategories) {
        this.isExpanded = !this.isExpanded;
      }
    },
    handleSelect() {
      // Emit the full path instead of just the node name
      this.$emit('toggle-select', this.fullPath);
    }
  },
  render() {
    const expandIcon = this.hasSubcategories
      ? h('span', {
          class: 'expand-icon',
          onClick: this.toggleExpand
        }, this.isExpanded ? '▼' : '▶')
      : h('span', { class: 'expand-icon-placeholder' });

    const countSpan = this.count > 0
      ? h('span', { class: 'category-count' }, `(${this.count})`)
      : null;

    const categoryNameClasses = this.hasSubcategories 
      ? 'category-name category-parent' 
      : 'category-name';

    const label = h('label', { 
      class: 'tree-node-label',
      title: this.fullPath // Show full path on hover
    }, [
      h('input', {
        type: 'checkbox',
        checked: this.isSelected,
        onChange: this.handleSelect,
        disabled: this.disabled
      }),
      h('span', { class: categoryNameClasses }, this.displayName),
      countSpan
    ]);

    const content = h('div', { class: 'tree-node-content' }, [
      expandIcon,
      label
    ]);

    const children = (this.isExpanded && this.hasSubcategories)
      ? h('div', { class: 'tree-node-children' },
          this.node.subcategories.map(child =>
            h(TreeNode, {
              key: child.name,
              node: child,
              selectedValues: this.selectedValues,
              searchQuery: this.searchQuery,
              disabled: this.disabled,
              counts: this.counts,
              parentPath: this.fullPath, // Pass the full path to children
              onToggleSelect: (event) => this.$emit('toggle-select', event)
            })
          )
        )
      : null;

    return h('div', { class: 'tree-node' }, [content, children]);
  }
};

export default {
  name: 'BackendCategoryTree',
  props: {
    categories: {
      type: Array,
      default: () => []
    },
    selectedValues: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    },
    showSearch: {
      type: Boolean,
      default: true
    },
    counts: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      searchQuery: ''
    };
  },
  computed: {
    filteredCategories() {
      if (!this.searchQuery) {
        return this.categories;
      }
      const query = this.searchQuery.toLowerCase();
      return this.filterTree(this.categories, query);
    }
  },
  methods: {
    filterTree(nodes, query) {
      return nodes.filter(node => {
        const nameMatches = node.name.toLowerCase().includes(query);
        const hasMatchingChildren = node.subcategories && 
          this.filterTree(node.subcategories, query).length > 0;
        return nameMatches || hasMatchingChildren;
      }).map(node => {
        if (node.subcategories && node.subcategories.length > 0) {
          return {
            ...node,
            subcategories: this.filterTree(node.subcategories, query)
          };
        }
        return node;
      });
    },
    handleToggleSelect(categoryName) {
      this.$emit('toggle-select', categoryName);
    }
  },
  components: {
    TreeNode
  }
};
</script>

<style>
.backend-category-tree {
  width: 100%;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.search-box {
  margin-bottom: 1rem;
}

.search-box input {
  width: 100%;
  padding: 0.625rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  transition: all 0.2s;
  background-color: #fff;
}

.search-box input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.search-box input::placeholder {
  color: #9ca3af;
}

.tree-container {
  max-height: 200px;
  overflow-y: auto;
  padding-right: 0.5rem;
}

.tree-node {
  position: relative;
}

.tree-node-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0;
  margin: 0;
}

.expand-icon {
  cursor: pointer;
  user-select: none;
  width: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.625rem;
  color: #4b5563;
  transition: color 0.2s;
  flex-shrink: 0;
}

.expand-icon:hover {
  color: #111827;
}

.expand-icon-placeholder {
  width: 16px;
  display: inline-block;
  flex-shrink: 0;
}

.tree-node-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  flex: 1;
  font-size: 0.875rem;
  color: #4b5563;
  padding: 0;
}

.tree-node-label input[type="checkbox"] {
  width: 1rem;
  height: 1rem;
  cursor: pointer;
  margin: 0;
  flex-shrink: 0;
}

.category-name {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.category-parent {
  font-weight: 600;
  color: #374151;
}

.category-count {
  color: #6b7280;
  font-size: 0.875rem;
  flex-shrink: 0;
  margin-left: auto;
}

.backend-category-tree .tree-node-children {
  margin-left: 30px !important;
  padding-left: 10px !important;
  border-left: 2px solid #d1d5db !important;
}

.backend-category-tree .tree-container::-webkit-scrollbar {
  width: 8px;
}

.tree-container::-webkit-scrollbar-track {
  background: #f9fafb;
  border-radius: 4px;
}

.tree-container::-webkit-scrollbar-thumb {
  background-color: #d1d5db;
  border-radius: 4px;
  border: 2px solid #f9fafb;
}

.tree-container::-webkit-scrollbar-thumb:hover {
  background-color: #9ca3af;
}
</style>
